import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Apartments from './pages/Apartments';
import ApartmentUsers from './pages/ApartmentUsers';
import Expenses from './pages/Expenses';
import Contributions from './pages/Contributions';
import ExpenseCategories from './pages/ExpenseCategories';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import Profile from './pages/Profile';
import ResetPassword from './pages/ResetPassword';
import FunctionHall from './pages/FunctionHall';
import WaterBill from './pages/WaterBill';
import FlatBills from './pages/FlatBills';
import Festivals from './pages/Festivals';
import Help from './pages/Help';
import CorpusFund from './pages/CorpusFund';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
  components: {
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            minWidth: 'unset',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '6px 4px',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            margin: '8px',
            width: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            fontSize: '0.7rem',
            padding: '5px 10px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            fontSize: '0.6rem',
            height: '20px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          '@media (max-width: 600px)': {
            fontSize: '1.1rem',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiCardContent-root': {
              padding: '12px',
            },
            '& .MuiTypography-h4': {
              fontSize: '1.3rem',
            },
            '& .MuiTypography-h5': {
              fontSize: '1.1rem',
            },
            '& .MuiTypography-h6': {
              fontSize: '0.9rem',
            },
          },
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="apartments" element={<Apartments />} />
        <Route path="apartments/:id/users" element={<ApartmentUsers />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="contributions" element={<Contributions />} />
        <Route path="categories" element={<ExpenseCategories />} />
        <Route path="reports" element={<Reports />} />
        <Route path="activity" element={<AuditLogs />} />
        <Route path="profile" element={<Profile />} />
        <Route path="function-hall" element={<FunctionHall />} />
        <Route path="water-bill" element={<WaterBill />} />
        <Route path="flat-bills" element={<FlatBills />} />
        <Route path="festivals" element={<Festivals />} />
        <Route path="help" element={<Help />} />
        <Route path="corpus-fund" element={<CorpusFund />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
